import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from '../hooks/use-toast';
import { getFromLocalStorage } from '../mock';
import {
  Database, Download, Upload, Trash2, RotateCcw, Shield, Clock,
  HardDrive, Loader2, AlertTriangle, CheckCircle, FileDown
} from 'lucide-react';

const BACKEND_URL = ''; // same-origin: reverse proxy routes /api/* to backend
const getHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${getFromLocalStorage('accessToken')}`,
});

const formatSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const TYPE_LABELS = {
  manual: ['Manuel', 'bg-blue-600'],
  auto: ['Otomatik', 'bg-gray-600'],
  pre_restore_safety: ['Guvenlik', 'bg-yellow-600'],
};

const BackupModule = () => {
  const [backups, setBackups] = useState([]);
  const [hasUploadedFiles, setHasUploadedFiles] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(null);

  const loadBackups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/backups`, { headers: getHeaders() });
      if (res.ok) {
        const d = await res.json();
        setBackups(d.backups || []);
        setHasUploadedFiles(d.hasUploadedFiles || false);
      }
    } catch (e) { console.error('Load backup list error:', e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadBackups(); }, [loadBackups]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/backups/create`, {
        method: 'POST', headers: getHeaders(),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Hata');
      toast({ title: "Yedek Olusturuldu", description: `${d.backup?.total_records || 0} kayit yedeklendi.` });
      loadBackups();
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
    setCreating(false);
  };

  const handleUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/backups/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${getFromLocalStorage('accessToken')}` },
          body: formData,
        });
        const d = await res.json();
        if (!res.ok) throw new Error(d.detail || 'Hata');
        toast({ title: "Yedek Yuklendi", description: `${file.name} basariyla yuklendi.` });
        loadBackups();
      } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
    };
    input.click();
  };

  const handleDownload = async (filename) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/backups/download/${filename}`, {
        headers: { 'Authorization': `Bearer ${getFromLocalStorage('accessToken')}` },
      });
      if (!res.ok) throw new Error('Indirme basarisiz');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`"${filename}" silinsin mi?`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/backups/${filename}`, {
        method: 'DELETE', headers: getHeaders(),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Hata'); }
      toast({ title: "Silindi", description: `${filename} silindi.` });
      loadBackups();
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
  };

  const handleRestore = async (filename) => {
    setRestoring(filename);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/backups/restore`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ filename }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Geri yukleme basarisiz');
      toast({
        title: "Geri Yukleme Basarili",
        description: `${d.totalRecords || 0} kayit geri yuklendi. Guvenlik yedegi: ${d.safetyBackup}`,
      });
      setConfirmRestore(null);
      loadBackups();
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
    setRestoring(null);
  };

  const totalRecords = backups[0]?.total_records || backups[0]?.record_counts
    ? Object.values(backups[0]?.record_counts || {}).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="space-y-4" data-testid="backup-module">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Card className="bg-gray-800/70 border-gray-700/40">
          <CardContent className="p-3 text-center">
            <Database className="h-5 w-5 text-blue-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-white">{backups.length}</p>
            <p className="text-gray-400 text-[10px]">Toplam Yedek</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/70 border-gray-700/40">
          <CardContent className="p-3 text-center">
            <HardDrive className="h-5 w-5 text-green-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-white">{formatSize(backups.reduce((a, b) => a + (b.file_size || 0), 0))}</p>
            <p className="text-gray-400 text-[10px]">Toplam Boyut</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/70 border-gray-700/40">
          <CardContent className="p-3 text-center">
            <Clock className="h-5 w-5 text-brand mx-auto mb-1" />
            <p className="text-sm font-bold text-white truncate">
              {backups[0]?.created_at ? new Date(backups[0].created_at).toLocaleDateString('tr-TR') : '-'}
            </p>
            <p className="text-gray-400 text-[10px]">Son Yedek</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/70 border-gray-700/40">
          <CardContent className="p-3 text-center">
            <Shield className="h-5 w-5 text-purple-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-white">{backups.filter(b => b.backup_type === 'auto').length}</p>
            <p className="text-gray-400 text-[10px]">Otomatik</p>
          </CardContent>
        </Card>
      </div>

      {/* File upload warning */}
      {hasUploadedFiles && (
        <div className="bg-yellow-900/30 border border-yellow-700/50 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-yellow-400 mt-0.5 flex-shrink-0" />
          <p className="text-yellow-300 text-xs">
            Sistemde yuklu dosyalar/fotograflar mevcut. Veritabani yedegi bu dosyalari icermez.
            Dosya yedegi icin sunucu uzerinden ayrica yedekleme yapin.
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 flex-wrap">
        <Button onClick={handleCreate} disabled={creating}
          className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8" data-testid="create-backup-btn">
          {creating ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Database className="h-3 w-3 mr-1" />}
          {creating ? 'Olusturuluyor...' : 'Yedek Olustur'}
        </Button>
        <Button onClick={handleUpload} variant="outline"
          className="border-gray-600 text-gray-300 text-xs h-8" data-testid="upload-backup-btn">
          <Upload className="h-3 w-3 mr-1" />Yedek Yukle
        </Button>
        <Button onClick={loadBackups} variant="outline" disabled={loading}
          className="border-gray-600 text-gray-300 text-xs h-8">
          <RotateCcw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />Yenile
        </Button>
      </div>

      {/* Backup List */}
      <div className="space-y-2">
        {backups.length === 0 ? (
          <div className="text-center text-gray-400 py-8">
            <Database className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Henuz yedek yok. Ilk yedeginizi olusturun.</p>
          </div>
        ) : backups.map((b) => {
          const [typeLabel, typeCls] = TYPE_LABELS[b.backup_type] || ['Bilinmeyen', 'bg-gray-600'];
          const counts = b.record_counts || {};
          return (
            <Card key={b.filename} className="bg-gray-800/60 border-gray-700/40" data-testid={`backup-row-${b.filename}`}>
              <CardContent className="p-3">
                <div className="flex flex-col md:flex-row md:items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white text-sm font-semibold truncate">{b.filename}</p>
                      <Badge className={`${typeCls} text-white text-[10px]`}>{typeLabel}</Badge>
                      {b.checksum && <Badge className="bg-green-800 text-green-300 text-[10px]"><CheckCircle className="h-2.5 w-2.5 mr-0.5" />Dogrulanmis</Badge>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                      <span>{b.created_at ? new Date(b.created_at).toLocaleString('tr-TR') : '-'}</span>
                      <span>{formatSize(b.file_size)}</span>
                      <span>{b.total_records || Object.values(counts).reduce((a, c) => a + c, 0)} kayit</span>
                      {b.created_by && <span className="text-gray-500">({b.created_by})</span>}
                    </div>
                    {Object.keys(counts).length > 0 && (
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {Object.entries(counts).filter(([, v]) => v > 0).slice(0, 6).map(([k, v]) => (
                          <Badge key={k} variant="outline" className="border-gray-600 text-gray-400 text-[10px]">{k}: {v}</Badge>
                        ))}
                        {Object.keys(counts).filter(k => counts[k] > 0).length > 6 && (
                          <Badge variant="outline" className="border-gray-600 text-gray-500 text-[10px]">+{Object.keys(counts).filter(k => counts[k] > 0).length - 6}</Badge>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => handleDownload(b.filename)}
                      className="border-gray-600 text-gray-300 text-xs h-7" data-testid={`download-${b.filename}`}>
                      <FileDown className="h-3 w-3 mr-1" />Indir
                    </Button>
                    <Button size="sm" onClick={() => setConfirmRestore(b)}
                      className="bg-brand hover:bg-brand text-white text-xs h-7" data-testid={`restore-${b.filename}`}>
                      <RotateCcw className="h-3 w-3 mr-1" />Geri Yukle
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(b.filename)}
                      className="text-xs h-7" data-testid={`delete-${b.filename}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Restore Confirmation Modal */}
      {confirmRestore && (
        <Dialog open={true} onOpenChange={() => { if (!restoring) setConfirmRestore(null); }}>
          <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />Geri Yukleme Onayi
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Bu islem geri alinamaz.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="bg-red-900/30 border border-red-700/50 rounded-lg p-3">
                <p className="text-red-300 text-sm font-semibold">
                  Bu islem mevcut veritabanini secilen yedekle TAMAMEN degistirecektir.
                </p>
                <p className="text-red-400 text-xs mt-1">
                  Mevcut durumun guvenlik yedegi otomatik olarak olusturulacaktir.
                </p>
              </div>
              <div className="bg-gray-800 rounded-lg p-3">
                <p className="text-white text-sm font-semibold">{confirmRestore.filename}</p>
                <p className="text-gray-400 text-xs">
                  {confirmRestore.created_at ? new Date(confirmRestore.created_at).toLocaleString('tr-TR') : '-'}
                  {' '} - {confirmRestore.total_records || 0} kayit - {formatSize(confirmRestore.file_size)}
                </p>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={() => setConfirmRestore(null)} disabled={!!restoring}
                  variant="outline" className="flex-1 border-gray-600 text-gray-300">
                  Iptal
                </Button>
                <Button onClick={() => handleRestore(confirmRestore.filename)}
                  disabled={!!restoring}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-restore-btn">
                  {restoring ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
                  {restoring ? 'Geri Yukleniyor...' : 'Geri Yukle'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default BackupModule;
